import React, { useState, useEffect } from 'react';
import { QuickAddTrade } from './QuickAdd/QuickAddTrade';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { ActionButtonType } from './ActionButtons';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { BottomSheet } from '@/components/ui/bottom-sheet';
import { Search, Clipboard, Share, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/supabase/client';
import { debug } from '@/lib/utils';
import { SmartQuoteWrapper } from './SmartQuote';
import { Tradesperson } from '@/lib/types';

interface ActionPanelsProps {
  activePanel: ActionButtonType | null;
  onClose: () => void;
  onDataChange?: (trade: Tradesperson) => void;
  filteredTradespeople?: Tradesperson[];
  onActionButtonChange?: (show: boolean) => void;
}

export function ActionPanels({ activePanel, onClose, onDataChange, filteredTradespeople, onActionButtonChange }: ActionPanelsProps) {
  const { user } = useAuth();
  const navigate = useNavigate();


  if (!activePanel) return null;

  return (
    <>
      {/* Bottom Sheet for Add Trade */}
      <AddTradeBottomSheet
        isOpen={activePanel === 'add'}
        onClose={onClose}
        onDataChange={onDataChange}
      />

      {/* Other panels remain as inline cards */}
      <div className="my-3 transition-all duration-300">
        {activePanel === 'quote' && (
          <QuoteRequestPanel
            onClose={onClose}
            tradespeople={filteredTradespeople}
            onActionButtonChange={onActionButtonChange}
          />
        )}

        {activePanel === 'share' && (
          <ShareListPanel onClose={onClose} />
        )}

        {activePanel === 'find' && (
          <FindByAreaPanel onClose={onClose} />
        )}
      </div>
    </>
  );
}

function AddTradeBottomSheet({
  isOpen,
  onClose,
  onDataChange
}: {
  isOpen: boolean;
  onClose: () => void;
  onDataChange?: (trade: Tradesperson) => void;
}) {
  const { user } = useAuth();
  const [userPostcode, setUserPostcode] = useState<string | undefined>(undefined);

  // Fetch user's postcode from profile
  useEffect(() => {
    async function fetchUserPostcode() {
      if (!user) return;

      try {
        // Postcode comes from the authenticated user; the old Supabase query is
        // dead post-D1-migration (undefined `supabase` → threw at runtime).
        if (user.postcode) {
          setUserPostcode(user.postcode);
        }
      } catch (error) {
        debug.error('Error fetching user postcode:', error);
      }
    }

    fetchUserPostcode();
  }, [user]);

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={onClose}
      title="Add a Tradesperson"
      maxHeight="85vh"
      minHeight="85vh"
    >
      <QuickAddTrade
        onSuccess={() => {
          // Only trigger data change if we actually added something
          if (onDataChange) {
            onDataChange({} as Tradesperson);
          }
          onClose();
        }}
        userPostcode={userPostcode || ''}
        onInputChange={() => {}}
      />
    </BottomSheet>
  );
}

function QuoteRequestPanel({
  onClose,
  tradespeople: providedTradespeople,
  onActionButtonChange
}: {
  onClose: () => void,
  tradespeople?: Tradesperson[],
  onActionButtonChange?: (show: boolean) => void
}) {
  const { user } = useAuth();
  // Get tradespeople from TradeList to use with MobileTradeQuote
  const [tradespeople, setTradespeople] = useState<Tradesperson[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userPostcode, setUserPostcode] = useState<string | undefined>(undefined);



  // Fetch user's postcode from profile
  useEffect(() => {
    async function fetchUserPostcode() {
      if (!user) return;

      try {
        // Postcode comes from the authenticated user; the old Supabase query is
        // dead post-D1-migration (undefined `supabase` → threw at runtime).
        if (user.postcode) {
          setUserPostcode(user.postcode);
        }
      } catch (error) {
        debug.error('Error fetching user postcode:', error);
      }
    }

    fetchUserPostcode();
  }, [user]);

  // Use provided tradespeople if available, otherwise fetch from database
  useEffect(() => {
    // If tradespeople are provided from parent component, use those
    if (providedTradespeople && providedTradespeople.length > 0) {
      debug.log('QuoteRequestPanel: Using provided tradespeople', {
        count: providedTradespeople.length,
        source: 'parent component'
      });

      setTradespeople(providedTradespeople);
      setIsLoading(false);
      return;
    }

    // Otherwise fetch from database (fallback behavior)
    async function fetchTradespeople() {
      try {
        setIsLoading(true);
        // Fallback list via the Worker API (old Supabase query is dead).
        const { tradespeople: data } = await api.get<{ tradespeople: Tradesperson[] }>(
          '/tradespeople/popular?limit=20'
        );

        if (data) {
          debug.log('QuoteRequestPanel: Loaded tradespeople', { count: data.length });
          setTradespeople(data);
        } else {
          debug.log('QuoteRequestPanel: No tradespeople returned');
          // Still set empty array to ensure component renders properly
          setTradespeople([]);
        }
      } catch (error) {
        debug.error('Failed to load tradespeople for quote:', error);
        setError('Failed to load tradespeople');
        toast.error('Failed to load tradespeople');
      } finally {
        setIsLoading(false);
      }
    }

    fetchTradespeople();
  }, [providedTradespeople]);

  // Handle data loading callback from MobileTradeQuote if needed
  const handleDataLoaded = (loadedTradespeople: Tradesperson[]) => {
    debug.log('QuoteRequestPanel: Received data callback', {
      loadedCount: loadedTradespeople.length
    });
    // We can use this to update our state if needed
  };

  return (
    <div className="h-full bg-white">
      {error ? (
        <div className="p-4 bg-red-50 text-red-600 rounded-lg text-sm mx-4 mt-4">
          {error}
          <Button
            variant="ghost"
            size="sm"
            className="block mx-auto mt-2 text-xs text-red-700"
            onClick={() => setError(null)}
          >
            Try Again
          </Button>
        </div>
      ) : (
        <SmartQuoteWrapper
          tradespeople={tradespeople}
          isLoading={isLoading}
          postcode={userPostcode}
          onComplete={onClose}
          onDataLoaded={handleDataLoaded}
          onActionButtonChange={onActionButtonChange}
        />
      )}
    </div>
  );
}

function ShareListPanel({ onClose }: { onClose: () => void }) {
  const { user } = useAuth();
  const [copied, setCopied] = useState(false);
  const [username, setUsername] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch the user's profile to get the username
  useEffect(() => {
    if (!user) return;

    async function fetchUsername() {
      setIsLoading(true);
      try {
        // Username comes from the authenticated user (old Supabase query is dead).
        setUsername(user.username || null);
      } catch (error) {
        debug.error('Failed to load profile username:', error);
        toast.error('Could not load your profile information');
      } finally {
        setIsLoading(false);
      }
    }

    fetchUsername();
  }, [user]);

  // Generate share URL based on the user's username
  const getShareUrl = () => {
    if (!username) return '';

    // Use window.location to get the base URL and construct the share URL
    const baseUrl = window.location.origin;

    // Use clean URL structure regardless of domain
    return `${baseUrl}/${username}`;
  };

  // Handle copy to clipboard
  const handleCopyLink = async () => {
    const shareUrl = getShareUrl();

    if (!shareUrl) {
      toast.error("You don't have a shareable list yet");
      return;
    }

    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Your list link has been copied to clipboard");
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    } catch (error) {
      toast.error("Failed to copy link to clipboard");
    }
  };

  // Handle native share (for mobile devices)
  const handleNativeShare = async () => {
    const shareUrl = getShareUrl();

    if (!shareUrl) {
      toast.error("You don't have a shareable list yet");
      return;
    }

    // Check if the Web Share API is available
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'My TradesPurple List',
          text: 'Check out my trusted tradespeople list on TradesPurple!',
          url: shareUrl,
        });
        onClose();
      } catch (error) {
        // User probably canceled the share operation
        debug.error('Error sharing:', error);
      }
    } else {
      // Fall back to copy if share API is not available
      handleCopyLink();
    }
  };

  if (isLoading) {
    return (
      <div className="mx-4">
        <Card className="border-2 border-purple-900">
          <CardContent className="p-5 relative">
            {/* Back button */}
            <button
              onClick={onClose}
              className="absolute top-4 left-4 flex items-center justify-center rounded-md transition-colors border-2 px-2.5 py-1 text-sm bg-white hover:bg-purple-50 text-purple-800 border-purple-100 font-medium"
            >
              <X className="h-4 w-4 mr-1.5" />
              <span>Back</span>
            </button>

            <div className="pt-10">
              <h3 className="text-lg font-bold text-purple-900 mb-3">Share Your List</h3>
              <div className="flex justify-center items-center h-20">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-700"></div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!username) {
    return (
      <div className="mx-4">
        <Card className="border-2 border-purple-900">
          <CardContent className="p-5 relative">
            {/* Back button */}
            <button
              onClick={onClose}
              className="absolute top-4 left-4 flex items-center justify-center rounded-md transition-colors border-2 px-2.5 py-1 text-sm bg-white hover:bg-purple-50 text-purple-800 border-purple-100 font-medium"
            >
              <X className="h-4 w-4 mr-1.5" />
              <span>Back</span>
            </button>

            <div className="pt-10">
              <h3 className="text-lg font-bold text-purple-900 mb-3">Share Your List</h3>
              <p className="text-sm text-purple-700 mb-4">
                You need to set up a username before you can share your list. Go to your account settings to create a username.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-4">
      <Card className="border-2 border-purple-900">
        <CardContent className="p-5 relative">
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-3 right-3 flex items-center justify-center rounded-full w-7 h-7 bg-slate-100 text-purple-700 hover:bg-slate-200 transition-colors"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>

          <div>
            <h3 className="text-lg font-bold text-purple-900 mb-3">Share Your List</h3>
          <div
            className="bg-purple-50 p-3 rounded-lg mb-4 break-all cursor-pointer hover:bg-purple-100 transition-colors relative group"
            onClick={handleCopyLink}
            title="Click to copy URL"
          >
            <p className="text-sm text-purple-800 font-medium">
              {getShareUrl()}
            </p>
            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
              <div className="bg-purple-700 text-white text-xs px-2 py-1 rounded">
                Click to copy
              </div>
            </div>
          </div>
          <div className="flex space-x-3">
            <Button
              variant="secondary"
              onClick={handleCopyLink}
              className="flex-1 bg-purple-100 hover:bg-purple-200"
            >
              <Clipboard className="h-4 w-4 mr-2" />
              {copied ? "Copied!" : "Copy Link"}
            </Button>
            <Button
              onClick={handleNativeShare}
              className="flex-1 bg-purple-700 hover:bg-purple-800"
            >
              <Share className="h-4 w-4 mr-2" />
              Share
            </Button>
          </div>
        </div>
        </CardContent>
      </Card>
    </div>
  );
}

function FindByAreaPanel({ onClose }: { onClose: () => void }) {
  const [postcode, setPostcode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  // UK postcode first half validation (outward code)
  const validateOutwardCode = (code: string) => {
    // Simple regex for UK outward code validation
    const outwardCodeRegex = /^[A-Z]{1,2}[0-9]{1,2}$/i;
    return outwardCodeRegex.test(code);
  };

  // Process the postcode and navigate if valid
  const processPostcode = (postcodeValue: string) => {
    // Normalize the postcode (remove spaces, uppercase)
    let normalizedPostcode = postcodeValue.trim().toUpperCase();

    // Handle full postcodes by extracting just the outward code (first part)
    if (normalizedPostcode.includes(' ')) {
      normalizedPostcode = normalizedPostcode.split(' ')[0];
    } else if (normalizedPostcode.length > 4) {
      // If there's no space but it's longer than 4 chars, it might be a full postcode without space
      normalizedPostcode = normalizedPostcode.substring(0, 4);
    }

    if (!normalizedPostcode) {
      setError('Please enter a postcode');
      return false;
    }

    if (!validateOutwardCode(normalizedPostcode)) {
      setError('Please enter a valid postcode area (e.g. "HP22")');
      return false;
    }

    // Clear any previous errors
    setError(null);

    // Navigate directly to the postcode route
    navigate(`/${normalizedPostcode}`);
    onClose();

    return true;
  };

  // Handle form submission
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    processPostcode(postcode);
  };

  return (
    <div className="mx-4">
      <Card className="border-2 border-purple-900">
        <CardContent className="p-5 relative">
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-3 right-3 flex items-center justify-center rounded-full w-7 h-7 bg-slate-100 text-purple-700 hover:bg-slate-200 transition-colors"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>

          <div>
            <h3 className="text-lg font-bold text-purple-900 mb-3">Find Tradespeople By Postcode</h3>
          <form onSubmit={handleSearch}>
            <div className="mb-4">
              <div className="relative">
                <Input
                  id="area-postcode"
                  placeholder="Enter post/ZIP code"
                  className="border-2 border-purple-900 pl-10 py-6 text-base placeholder:text-purple-400"
                  value={postcode}
                  onChange={(e) => {
                    // Allow international postcodes and normalize case
                    const value = e.target.value.toUpperCase();
                    setPostcode(value);
                  }}
                  aria-label="Enter postcode to find tradespeople"
                  autoComplete="postal-code"
                  type="text"
                />
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <Search className="h-5 w-5 text-purple-500" />
                </div>
              </div>
              {error && (
                <p className="mt-2 text-red-600 text-sm">{error}</p>
              )}
              <p className="mt-1 text-gray-500 text-xs">
                (Enter 3-4 characters of a UK postcode)
              </p>
            </div>
            <div className="flex justify-end space-x-3">
              <Button
                type="submit"
                className="bg-purple-700 hover:bg-purple-800"
              >
                Find Tradespeople
              </Button>
            </div>
          </form>
        </div>
        </CardContent>
      </Card>
    </div>
  );
}
